import {
  PITCH_WINDOW_EDGE_PADDING,
  PITCH_WINDOW_MIN_SIZE,
} from "@/constants/audioQuality";
import { AudioQualityCheck } from "@/types/defs/audioQuality/audioQualityCheck";
import { PitchRecommendation } from "@/types/defs/audioQuality/pitchRecommendation";
import { PitchShiftWindow } from "@/types/defs/audioQuality/pitchShiftWindow";
import { ModelPitchRange } from "@/types/defs/modelPitchRange";
import { calculatePitchShiftWindow } from "@/utils/audio/quality/pitchWindow/calculatePitchShiftWindow";

// Rule 1 — min width: if window ≤ PITCH_WINDOW_MIN_SIZE, expand symmetrically and centre the sweet spot. Rule 2 — edge padding: if the sweet spot sits within PITCH_WINDOW_EDGE_PADDING of either edge, expand that side.
const applyWindowUxAdjustments = (
  rawWindow: PitchShiftWindow,
  clampedPitch: number
): {
  recommendedPitch: number;
  window: PitchShiftWindow;
} => {
  const currentWindowSize = rawWindow.max - rawWindow.min;

  if (currentWindowSize < PITCH_WINDOW_MIN_SIZE) {
    const paddingPerSide = (PITCH_WINDOW_MIN_SIZE - currentWindowSize) / 2;
    const expandedMin = Math.round(rawWindow.min - paddingPerSide);
    const expandedWindow = {
      min: expandedMin,
      max: expandedMin + PITCH_WINDOW_MIN_SIZE,
    };
    return {
      recommendedPitch: Math.round(
        (expandedWindow.min + expandedWindow.max) / 2
      ),
      window: expandedWindow,
    };
  }

  const distanceFromLowEdge = clampedPitch - rawWindow.min;
  const distanceFromHighEdge = rawWindow.max - clampedPitch;

  if (distanceFromLowEdge <= PITCH_WINDOW_EDGE_PADDING) {
    return {
      recommendedPitch: clampedPitch + PITCH_WINDOW_EDGE_PADDING,
      window: {
        min: rawWindow.min - PITCH_WINDOW_EDGE_PADDING,
        max: rawWindow.max,
      },
    };
  }

  if (distanceFromHighEdge <= PITCH_WINDOW_EDGE_PADDING) {
    return {
      recommendedPitch: clampedPitch - PITCH_WINDOW_EDGE_PADDING,
      window: {
        min: rawWindow.min,
        max: rawWindow.max + PITCH_WINDOW_EDGE_PADDING,
      },
    };
  }

  return { recommendedPitch: clampedPitch, window: rawWindow };
};

export const computePitchWindow = (
  audioQualityCheck: AudioQualityCheck | null,
  modelPitchRange: ModelPitchRange
): PitchRecommendation => {
  if (audioQualityCheck === null || modelPitchRange === null) {
    return null;
  }
  const { pitchAnalysis } = audioQualityCheck;
  if (pitchAnalysis.status !== "ok") {
    return null;
  }

  const rawPitchWindow = calculatePitchShiftWindow(
    pitchAnalysis.userRange,
    modelPitchRange
  );

  const clampedPitch = Math.max(
    rawPitchWindow.min,
    Math.min(rawPitchWindow.max, pitchAnalysis.semitoneShift)
  );

  const { recommendedPitch, window } = applyWindowUxAdjustments(
    rawPitchWindow,
    clampedPitch
  );
  return { recommendedPitch, pitchShiftWindow: window };
};
